import {
    Accordion, AccordionDetails, AccordionSummary, Box, Chip, FormControl,
    InputLabel, MenuItem, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React, { useState } from 'react';
import {
    Bar, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import { FadedPaper, TrackedSelect } from '../../theme/StyledFields';
import VizToggle, { VizType } from '../team-report/VizToggle';
import { AthleteTimeline, CANONICAL_SLIDE_TITLES, formatDuration } from './types';

const TYPE_COLORS: Record<string, string> = {
    activity: '#4caf50',
    worksheet: '#2196f3',
    conversation: '#ff9800',
    session: '#9e9e9e',  // Session total (no slide breakdown)
};

interface Props {
    athletes: AthleteTimeline[];
}

const AthleteTimelineSection: React.FC<Props> = ({ athletes }) => {
    const [selectedAthleteId, setSelectedAthleteId] = useState<number | ''>('');
    const [viz, setViz] = useState<VizType>('bar');
    const theme = useTheme();

    const selectedAthlete = athletes.find((a) => a.athlete_id === selectedAthleteId) || null;

    return (
        <FadedPaper sx={{ p: 3, mb: 3 }} data-testid="section-athlete-timeline">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h5">4. Athlete Drill-Down</Typography>
                <VizToggle value={viz} options={['bar', 'table']} onChange={setViz} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select an athlete to see their slide durations within each completed session, sorted by duration.
            </Typography>

            <FormControl sx={{ minWidth: 250, mb: 3 }} size="small">
                <InputLabel>Athlete</InputLabel>
                <TrackedSelect
                    value={selectedAthleteId}
                    label="Athlete"
                    onChange={(e) => setSelectedAthleteId(e.target.value as number)}
                >
                    {athletes.map((a) => (
                        <MenuItem key={a.athlete_id} value={a.athlete_id}>
                            {a.athlete_name} ({a.sessions.length} sessions)
                        </MenuItem>
                    ))}
                </TrackedSelect>
            </FormControl>

            {!selectedAthlete && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    Select an athlete above to view their session timeline.
                </Typography>
            )}

            {selectedAthlete && selectedAthlete.sessions.map((session) => {
                const barData = session.slides
                    .sort((a, b) => b.duration_seconds - a.duration_seconds)
                    .map((sl) => ({
                        label: sl.slide_type === 'session' || sl.slide_number === 0
                            ? 'Session total'
                            : CANONICAL_SLIDE_TITLES[sl.slide_number]
                                ? `${sl.slide_number}. ${CANONICAL_SLIDE_TITLES[sl.slide_number].split(' ').slice(0, 2).join(' ')}`
                                : `Slide ${sl.slide_number}`,
                        duration: Math.round(sl.duration_seconds / 60 * 10) / 10,
                        durationRaw: sl.duration_seconds,
                        type: sl.slide_type,
                        fill: TYPE_COLORS[sl.slide_type] || theme.palette.primary.main,
                    }));

                return (
                    <Accordion key={session.session_number} defaultExpanded={session.session_number === 1}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    Session {session.session_number}
                                </Typography>
                                {session.session_duration_seconds != null && (
                                    <Chip
                                        label={formatDuration(session.session_duration_seconds)}
                                        size="small"
                                        variant="outlined"
                                    />
                                )}
                                <Chip label={`${session.slides.length} slides`} size="small" variant="outlined" />
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            {viz === 'bar' && barData.length > 0 && (
                                <ResponsiveContainer width="100%" height={Math.max(200, barData.length * 35)}>
                                    <BarChart data={barData} layout="vertical" margin={{ left: 130 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis type="number" label={{ value: 'Minutes', position: 'bottom', offset: 0 }} />
                                        <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={120} />
                                        <Tooltip
                                            formatter={(val: number) => [`${val} min`, 'Duration']}
                                        />
                                        <Bar dataKey="duration" name="Duration" fill={theme.palette.primary.main} radius={[0, 4, 4, 0]}>
                                            {barData.map((entry, idx) => (
                                                <Cell key={idx} fill={entry.fill} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            )}

                            {viz === 'table' && (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Slide</TableCell>
                                                <TableCell>Title</TableCell>
                                                <TableCell>Type</TableCell>
                                                <TableCell align="right">Duration</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {session.slides
                                                .sort((a, b) => b.duration_seconds - a.duration_seconds)
                                                .map((sl) => (
                                                    <TableRow key={sl.slide_type === 'session' ? `session-${session.session_number}` : sl.slide_number}>
                                                        <TableCell>{sl.slide_type === 'session' || sl.slide_number === 0 ? '—' : sl.slide_number}</TableCell>
                                                        <TableCell>
                                                            {sl.slide_type === 'session' || sl.slide_number === 0
                                                                ? 'Session total'
                                                                : CANONICAL_SLIDE_TITLES[sl.slide_number] || `Slide ${sl.slide_number}`}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={sl.slide_type}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: TYPE_COLORS[sl.slide_type] || theme.palette.grey[400],
                                                                    color: '#fff',
                                                                    textTransform: 'capitalize',
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 500 }}>
                                                            {formatDuration(sl.duration_seconds)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </FadedPaper>
    );
};

export default AthleteTimelineSection;
